import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Modal, ModalTitle, useValidation } from '@mesa-labs/mesa-ui';

import { ModalContent } from '../dashboard/common/GetPaidNowModals/ModalComponents';
import cypressSelectors from '../../../cypress-selectors.json';
import { type DemoNavigationTarget } from '../../redux/slices/demoMode';

const CustomModalTitle = styled(ModalTitle)`
  color: ${(props) => props.theme.colors.Gray900};
  font-size: 24px;
  margin-bottom: 16px;
  text-align: left;  
`;

const ModalButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const EntryPointSection = styled.fieldset`
  border: none;
  margin-bottom: 24px;
  margin-top: 16px;
  padding: 0;
`;

const EntryPointLabel = styled.legend`
  color: #0E121B;
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.084px;
  line-height: 20px;
  margin-bottom: 4px;
`;

const DestinationGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  margin-top: 8px;
`;

const DestinationLabel = styled.label`
  align-items: center;
  color: #0E121B;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
`;

const DestinationRadio = styled.input`
  accent-color: #4054ED;
  cursor: pointer;
  height: 16px;
  width: 16px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: ({ password, navigateTo }: { password: string; navigateTo: DemoNavigationTarget }) => void;
  isLoading: boolean;
}

function DemoModePasswordModal({ open, onClose, onSubmit, isLoading }: Props): ReactElement {
  const [password, setPassword] = useState('');
  const [navigateTo, setNavigateTo] = useState<DemoNavigationTarget>('onboarding');
  const { results: validationResults, passed } = useValidation({ password });

  const handleClose = () => {
    setNavigateTo('onboarding');
    onClose();
  };

  return (
    <Modal
      modalRootId="modal-root"
      open={open}
      onClose={handleClose}
      width={440}
    >
      <ModalContent>
        <CustomModalTitle>Enter demo mode password</CustomModalTitle>
        <Input
          dataCy={cypressSelectors.demoModePasswordInput}
          autoFocus
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(evt) => setPassword(evt.target.value)}
          placeholder="Enter your password"
          label="Password"
          validationResult={validationResults.password}
          required
        />

        <EntryPointSection>
          <EntryPointLabel>Entry point</EntryPointLabel>
          <DestinationGroup>
            <DestinationLabel>
              <DestinationRadio
                type="radio"
                name="navigateTo"
                value="onboarding"
                checked={navigateTo === 'onboarding'}
                onChange={() => setNavigateTo('onboarding')}
              />
              Onboarding
            </DestinationLabel>
            <DestinationLabel>
              <DestinationRadio
                type="radio"
                name="navigateTo"
                value="dashboard"
                checked={navigateTo === 'dashboard'}
                onChange={() => setNavigateTo('dashboard')}
              />
              Dashboard
            </DestinationLabel>
          </DestinationGroup>
        </EntryPointSection>

        <ModalButtonContainer>
          <Button
            text="Cancel"
            onClick={handleClose}
            color="secondary"
            width="100%"
          />
          <Button
            dataCy={cypressSelectors.demoModePasswordSubmitButton}
            text="Submit"
            disabled={!passed}
            onClick={async () => onSubmit({ password, navigateTo })}
            color="primary"
            width="100%"
            isLoading={isLoading}
          />
        </ModalButtonContainer>
      </ModalContent>
    </Modal>
  );
}

export default DemoModePasswordModal;
